import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { GrLocationPin } from 'react-icons/gr';
import { RiArrowDropDownFill } from 'react-icons/ri';
import { FaMinus } from 'react-icons/fa';
import { TiPlus } from 'react-icons/ti';
import { formatDate } from '../utils';

/**
 * Reservation form component for booking restaurant tables
 * @param {Object} props - Component props
 * @param {Object} props.data - Restaurant data
 * @param {string} props.selectedTime - Selected time slot
 * @param {string} props.date - Selected date
 * @param {number} props.guest - Number of guests
 * @param {Function} props.onTimeClick - Time selector click handler
 * @param {Function} props.onDateClick - Date selector click handler
 * @param {Function} props.onGuestChange - Guest count change handler
 * @param {Function} props.onBook - Book button click handler
 * @param {boolean} props.isAuthenticated - Authentication status
 * @param {Function} props.onLoginRedirect - Login redirect handler
 * @param {boolean} props.noButton - Hide the book button
 * @param {boolean} props.onlyButton - Show only the book button
 */
const ReservationForm = ({
  data,
  selectedTime,
  date,
  guest,
  onTimeClick,
  onDateClick,
  onGuestChange,
  onBook,
  isAuthenticated,
  onLoginRedirect,
  noButton = false,
  onlyButton = false,
}) => {
  const handleGuestDecrease = () => {
    if (guest > 1) {
      onGuestChange(guest - 1);
    }
  };

  const handleGuestIncrease = () => {
    onGuestChange(guest + 1);
  };

  const handleBookClick = () => {
    if (isAuthenticated) {
      onBook();
    } else {
      onLoginRedirect();
    }
  };

  if (!data) return null;

  // If onlyButton is true, show only the book button
  if (onlyButton) {
    return (
      <Button
        onClick={handleBookClick}
        disabled={!selectedTime || !date || !guest}
        variant="contained"
        sx={{ mt: 3, borderRadius: '30px', py: 2 }}
        fullWidth
      >
        Book
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #d7d7d7',
        borderRadius: '10px',
        p: 3,
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: {
            lg: '18px',
            md: '18px',
            sm: '16px',
            xs: '14px',
          },
        }}
      >
        Dine at {data?.RestaurantName}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 1,
          mt: 1,
        }}
      >
        <GrLocationPin style={{ color: '#BC172F' }} />
        <Typography
          sx={{
            fontSize: {
              lg: '14px',
              md: '14px',
              sm: '12px',
              xs: '12px',
            },
          }}
        >
          {data?.LGA}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 3,
          border: '1px solid #d7d7d7',
          borderRadius: '10px',
          p: 2,
        }}
      >
        <Grid container>
          {/* Time Selector */}
          {!noButton && (
            <Grid
              item
              lg={6}
              md={6}
              sm={6}
              xs={6}
              sx={{ display: 'grid', placeItems: 'center' }}
            >
              <MenuItem sx={{ p: 0, width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    pl: 2,
                    pb: 0.5,
                    boxSizing: 'border-box',
                  }}
                  onClick={onTimeClick}
                >
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 300 }}>
                      Time
                    </Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                      {selectedTime || 'Select Time'}
                    </Typography>
                  </Box>
                  <RiArrowDropDownFill />
                </Box>
              </MenuItem>
            </Grid>
          )}

          {/* Date Selector */}
          <Grid
            item
            lg={noButton ? 12 : 6}
            md={noButton ? 12 : 6}
            sm={noButton ? 12 : 6}
            xs={noButton ? 12 : 6}
            sx={{ display: 'grid', placeItems: 'center' }}
          >
            <MenuItem sx={{ p: 0, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  borderLeft: !noButton && '1px solid #d7d7d7',
                  pl: 2,
                  pb: 0.5,
                  boxSizing: 'border-box',
                }}
                onClick={onDateClick}
              >
                <Box>
                  <Typography sx={{ fontSize: '12px', fontWeight: 300 }}>
                    Date
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                    {formatDate(date)}
                  </Typography>
                </Box>
                <RiArrowDropDownFill />
              </Box>
            </MenuItem>
          </Grid>

          {/* Guest Counter */}
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{ display: 'grid', placeItems: 'center' }}
          >
            <Box sx={{ borderTop: '1px solid #d7d7d7', width: '100%' }}>
              <TextField
                fullWidth
                margin="dense"
                value={guest}
                InputProps={{
                  style: {
                    fontFamily: 'lato',
                    fontSize: '13px',
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleGuestDecrease}
                        sx={{
                          borderRadius: '8px',
                          border: '0.5px solid #d7d7d7',
                        }}
                      >
                        <FaMinus style={{ fontSize: '12px' }} />
                      </IconButton>
                      <IconButton
                        onClick={handleGuestIncrease}
                        sx={{
                          borderRadius: '8px',
                          border: '0.5px solid #d7d7d7',
                          ml: 2,
                        }}
                      >
                        <TiPlus style={{ fontSize: '12px' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {!noButton && (
        <Button
          onClick={handleBookClick}
          disabled={!selectedTime || !date || !guest}
          variant="contained"
          sx={{ mt: 3, borderRadius: '30px', py: 2 }}
          fullWidth
        >
          Book
        </Button>
      )}
    </Box>
  );
};

ReservationForm.propTypes = {
  data: PropTypes.object,
  selectedTime: PropTypes.string,
  date: PropTypes.string.isRequired,
  guest: PropTypes.number.isRequired,
  onTimeClick: PropTypes.func.isRequired,
  onDateClick: PropTypes.func.isRequired,
  onGuestChange: PropTypes.func.isRequired,
  onBook: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  onLoginRedirect: PropTypes.func.isRequired,
  noButton: PropTypes.bool,
  onlyButton: PropTypes.bool,
};

export default ReservationForm;
